import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '../lib/api.js'
import { useAuth } from './useAuth.js'
import type { LearnerProfile } from '../types/index.js'

export function useLearnerProfile() {
  const { session } = useAuth()
  const [profile, setProfile] = useState<LearnerProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchProfile = useCallback(async () => {
    if (!session) { setProfile(null); return }
    setLoading(true)
    try {
      const res = await apiFetch<{ data: LearnerProfile }>('/api/public/profile')
      setProfile(res.data)
      setError(null)
    } catch (err: any) {
      if (err?.status === 404) {
        setProfile(null)
        setError(null)
      } else {
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    } finally {
      setLoading(false)
    }
  }, [session])

  useEffect(() => { fetchProfile() }, [fetchProfile])

  const upsertProfile = useCallback(async (input: { fullName: string; headline?: string | null }) => {
    const res = await apiFetch<{ data: LearnerProfile }>('/api/public/profile', {
      method: 'PUT',
      body: input,
    })
    setProfile(res.data)
    return res.data
  }, [])

  return { profile, loading, error, upsertProfile, refetch: fetchProfile }
}
